// v77.48 — One-shot achievement nudges.
//
// Why: small celebrations of first-time milestones (first co-parent
// invited, first court bundle generated, first journal entry, etc.)
// are a cheap, low-noise way to make the app feel rewarding without
// nagging users. They fire ONCE per user per key, then go quiet.
//
// Storage: each key is namespaced as `cpc.achv.<key>.<userId>` in
// localStorage. The flag is set on first display so a remount within
// the same session doesn't re-fire. Per-user so two parents on the
// same device each get their own first-time experience.
//
// Usage:
//   import { celebrate } from "@/lib/celebrate";
//   celebrate({
//     key: "first-court-bundle",
//     userId: user.id,
//     title: "First court bundle generated",
//     body: "Nicely done — your records are now court-ready.",
//   });
//
// The renderer is sonner's toast.custom so we control colour and
// avoid sonner's low-contrast `description` styling. The toast is
// styled with a celebratory teal accent + Sparkles icon so it's
// visually distinct from regular feedback toasts.

import { Sparkles } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const seenKey = (key, userId) => `cpc.achv.${key}.${userId || "anon"}`;

export function hasCelebrated(key, userId) {
  try { return localStorage.getItem(seenKey(key, userId)) === "1"; }
  catch { return false; }
}

export function markCelebrated(key, userId) {
  try { localStorage.setItem(seenKey(key, userId), "1"); }
  catch { /* ignore */ }
}

export function celebrate({ key, userId, title, body, icon: IconCmp = Sparkles, duration = 9000, testid = "achievement-toast" }) {
  if (!key) return false;
  if (hasCelebrated(key, userId)) return false;
  // Mark immediately so a fast remount doesn't re-fire even if the
  // toast itself is dismissed before onAutoClose fires.
  markCelebrated(key, userId);
  toast.custom(
    (id) => (
      <div
        className="bg-white border border-[#A6D7D9] rounded-md shadow-lg p-4 max-w-sm"
        data-testid={testid}
      >
        <div className="flex items-start gap-3">
          <span className="w-8 h-8 rounded-sm bg-[#D6F0EF] text-[#116B6E] inline-flex items-center justify-center flex-shrink-0 mt-0.5">
            <IconCmp className="w-4 h-4" strokeWidth={1.6} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base font-medium text-[#1A1F22]">
              {title}
            </div>
            {body && (
              <p className="text-sm text-[#3F595B] mt-1 leading-snug">
                {body}
              </p>
            )}
            <button
              type="button"
              onClick={() => toast.dismiss(id)}
              className="mt-3 inline-flex items-center text-sm font-medium text-[#116B6E] hover:text-[#0F5658]"
              data-testid={`${testid}-close`}
            >
              Thanks
            </button>
          </div>
        </div>
      </div>
    ),
    { duration },
  );
  return true;
}
